import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import apiService from '../../services/api';
import { Departamento, Provincia, Distrito, Direccion } from '../../types/comercio';

interface UbigeoSelectorProps {
  value: {
    departamento_id: string;
    provincia_id: string;
    distrito_id: string;
  };
  onChange: (ubigeo: {
    departamento_id: string;
    provincia_id: string;
    distrito_id: string;
  }) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

const UbigeoSelector: React.FC<UbigeoSelectorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  required = false
}) => {
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [distritos, setDistritos] = useState<Distrito[]>([]);
  
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingDistritos, setLoadingDistritos] = useState(false);
  
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Cargar departamentos al montar el componente
  useEffect(() => {
    loadDepartamentos();
  }, []);

  // Cargar provincias cuando cambia el departamento
  useEffect(() => {
    if (value.departamento_id) {
      loadProvincias(value.departamento_id);
    } else {
      setProvincias([]);
      setDistritos([]);
    }
  }, [value.departamento_id]);

  // Cargar distritos cuando cambia la provincia
  useEffect(() => {
    if (value.provincia_id) {
      loadDistritos(value.provincia_id);
    } else {
      setDistritos([]);
    }
  }, [value.provincia_id]);

  const loadDepartamentos = async () => {
    setLoadingDepartamentos(true);
    setErrorMsg('');
    
    try {
      const response = await apiService.getDepartamentos();
      if (response.success) {
        setDepartamentos(response.data);
      } else {
        setErrorMsg('Error cargando departamentos');
      }
    } catch (error) {
      console.error('Error cargando departamentos:', error);
      setErrorMsg('Error cargando departamentos');
    } finally {
      setLoadingDepartamentos(false);
    }
  };

  const loadProvincias = async (departamentoId: string) => {
    setLoadingProvincias(true);
    
    try {
      const response = await apiService.getProvincias(departamentoId);
      if (response.success) {
        setProvincias(response.data);
      } else {
        setErrorMsg('Error cargando provincias');
      }
    } catch (error) {
      console.error('Error cargando provincias:', error);
      setErrorMsg('Error cargando provincias');
    } finally {
      setLoadingProvincias(false);
    }
  };

  const loadDistritos = async (provinciaId: string) => {
    setLoadingDistritos(true);
    
    try {
      const response = await apiService.getDistritos(provinciaId);
      if (response.success) {
        setDistritos(response.data);
      } else {
        setErrorMsg('Error cargando distritos');
      }
    } catch (error) {
      console.error('Error cargando distritos:', error);
      setErrorMsg('Error cargando distritos');
    } finally {
      setLoadingDistritos(false);
    }
  };

  const handleDepartamentoChange = (departamentoId: string) => {
    onChange({
      departamento_id: departamentoId,
      provincia_id: '',
      distrito_id: ''
    });
  };

  const handleProvinciaChange = (provinciaId: string) => {
    onChange({
      ...value,
      provincia_id: provinciaId,
      distrito_id: ''
    });
  };

  const handleDistritoChange = (distritoId: string) => {
    onChange({
      ...value,
      distrito_id: distritoId
    });
  };

  return (
    <Box>
      {(error || errorMsg) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || errorMsg}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Departamento */}
        <FormControl fullWidth required={required} error={!!error} disabled={disabled}>
          <InputLabel>Departamento</InputLabel>
          <Select
            value={value.departamento_id}
            label="Departamento"
            onChange={(e) => handleDepartamentoChange(e.target.value)}
            endAdornment={loadingDepartamentos && <CircularProgress size={20} />}
          >
            <MenuItem value="">
              <em>Seleccionar departamento</em>
            </MenuItem>
            {departamentos.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Provincia */}
        <FormControl 
          fullWidth 
          required={required} 
          error={!!error} 
          disabled={disabled || !value.departamento_id}
        >
          <InputLabel>Provincia</InputLabel>
          <Select
            value={value.provincia_id}
            label="Provincia"
            onChange={(e) => handleProvinciaChange(e.target.value)}
            endAdornment={loadingProvincias && <CircularProgress size={20} />}
          >
            <MenuItem value="">
              <em>Seleccionar provincia</em>
            </MenuItem>
            {provincias.map((prov) => (
              <MenuItem key={prov.id} value={prov.id}>
                {prov.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Distrito */}
        <FormControl 
          fullWidth 
          required={required} 
          error={!!error} 
          disabled={disabled || !value.provincia_id}
        >
          <InputLabel>Distrito</InputLabel>
          <Select
            value={value.distrito_id}
            label="Distrito"
            onChange={(e) => handleDistritoChange(e.target.value)}
            endAdornment={loadingDistritos && <CircularProgress size={20} />}
          >
            <MenuItem value="">
              <em>Seleccionar distrito</em>
            </MenuItem>
            {distritos.map((dist) => (
              <MenuItem key={dist.id} value={dist.id}>
                {dist.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default UbigeoSelector;
